
<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { Form, Field, ErrorMessage } from 'vee-validate';
import type { FormContext } from 'vee-validate';
import {
  BuildingOffice2Icon,
  CalendarDaysIcon,
  CheckCircleIcon,
  ClipboardDocumentCheckIcon,
  ClockIcon,
  CpuChipIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  TrashIcon,
  UserIcon,
  WrenchScrewdriverIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';
import { IMaintenance } from '@/interfaces/entities/maintenance';
import { IMaintenanceComment } from '@/interfaces/entities/maintenance_comment';
import { ISensor } from '@/interfaces/entities/sensor';
import { UserRoles } from '@/interfaces/enums';
import { useUserStore } from '@/store/user';
import { useI18n } from 'vue-i18n';
import { formatDateWithTime } from '@/utils/date/date-time';
import Dropdown from '@/components/Globals/Inputs/Dropdown.vue';
import DropdownSensors from '@/components/Globals/Inputs/DropdownSensors.vue';
import MaintenanceOpenEditSchema from '@/schemas/MaintenanceOpenEditSchema';
import { useMaintenanceStore } from '@/store/maintenance';
import { useCompanyStore } from '@/store/company';
import { useSensorStore } from '@/store/sensor';
import { storeToRefs } from 'pinia';
import type { IUpdateMaintenance } from '@/interfaces/api/maintenance';
import type { ICompany } from '@/interfaces/entities/company';

const { t } = useI18n();
const userStore = useUserStore();
const showDestroyConfirm = ref(false);

const maintenanceStore = useMaintenanceStore();
const { getMaintenanceTypes, updateMaintenance } = maintenanceStore;
const { listMaintenanceTypes } = storeToRefs(maintenanceStore);

const companyStore = useCompanyStore();
const { currentCompany, getCompanies, getAllCompanies } = storeToRefs(companyStore);
const { fetchAllCompanies } = companyStore;

const sensorStore = useSensorStore();
const { fetch: fetchSensors, fetchSensorsByCompanyId } = sensorStore;
const { listSensors, getSensorsForCompany } = storeToRefs(sensorStore);

const isSuperAdminCompany = computed(() => Boolean(currentCompany.value?.super_admin));

const companies = computed(() => {
  if (isSuperAdminCompany.value) {
    const clients = getAllCompanies.value?.length ? getAllCompanies.value : []
    const current = currentCompany.value;
    if (current?.id != null && !clients.some((c) => c.id === current.id)) {
      return [current, ...clients]
    }
    return clients.length ? clients : getCompanies.value ?? []
  }
  return getCompanies.value ?? []
})
const selectedCompanyIdForSensors = ref<number | null>(null);

const selectedCompanyScopeForDropdown = computed((): ICompany | undefined => {
  const id = selectedCompanyIdForSensors.value;
  if (id == null) return undefined;
  return (
    companies.value.find((c) => c.id === id) ??
    (currentCompany.value?.id === id ? currentCompany.value : undefined)
  )
})

const editMaintenanceFormRef = ref<Pick<FormContext, 'setFieldValue'> | null>(null);

const props = defineProps<{
  maintenance: IMaintenance;
  section: string;
  actionsTaken: string;
  comments: IMaintenanceComment[];
  newCommentText: string;
  editingCommentId: number | null;
  currentTab: 'details' | 'comments';
  showDeleteConfirm: boolean;
  commentsLimit: number;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'start', id: number): void;
  (e: 'finish', id: number): void;
  (e: 'destroy', id: number): void;
  (e: 'add-comment'): void;
  (e: 'edit-comment'): void;
  (e: 'start-editing', comment: IMaintenanceComment): void;
  (e: 'cancel-editing'): void;
  (e: 'confirm-delete-comment', commentId: number): void;
  (e: 'confirm-delete'): void;
  (e: 'cancel-delete'): void;
  (e: 'load-more-comments'): void;
  (e: 'update:actionsTaken', value: string): void;
  (e: 'update:newCommentText', value: string): void;
  (e: 'update:currentTab', value: 'details' | 'comments'): void;
  (e: 'maintenance-updated', value: IMaintenance): void;
}>();

const isEditingOpenMaintenance = ref(false);
const isSubmittingEdit = ref(false);

const canDeleteMaintenance = computed(() => {
  const role = userStore.getCurrentUser?.role as string | undefined;
  const isAdminOrManager = role === UserRoles.ADMIN || role === UserRoles.MANAGER;
  return isAdminOrManager && props.section === 'open' && props.maintenance?.status === 'open';
});

const canEditOpenMaintenance = computed(() => {
  const role = userStore.getCurrentUser?.role as string | undefined;
  return role === UserRoles.ADMIN && props.section === 'open' && props.maintenance?.status === 'open';
});

const sensorCompanyNames = computed(() => {
  const linked = props.maintenance.sensor?.companies;
  if (!linked?.length) return '';
  return linked.map((c) => c.name).filter(Boolean).join(', ');
});

const maintenanceSensorHeadline = computed(() => {
  const name = props.maintenance.sensor?.name;
  if (name) return name;
  if (props.maintenance.machine?.name) return props.maintenance.machine.name;
  return t('maintenance_user_listing.modal.no_sensor_linked');
});

function priorityBadgeClass(priority: string | undefined): string {
  switch (priority) {
    case 'high':
      return 'bg-red/10 text-red';
    case 'medium':
      return 'bg-meta-6/20 text-black';
    case 'low':
      return 'bg-darkGreen/10 text-darkGreen';
    default:
      return 'bg-slate-200 text-slate-700';
  }
}

const priorityOptions = computed(() => [
  { id: 'low', name: t('maintenance_user_listing.priority.low') },
  { id: 'medium', name: t('maintenance_user_listing.priority.medium') },
  { id: 'high', name: t('maintenance_user_listing.priority.high') },
]);

const sensorOptions = computed((): ISensor[] => {
  if (isSuperAdminCompany.value) {
    const id = selectedCompanyIdForSensors.value;
    if (id == null) return [];
    return getSensorsForCompany.value(id);
  }
  return listSensors.value;
});

function inferInitialCompanyIdForSensors(): number | null {
  const linked = props.maintenance.sensor?.companies?.[0];
  if (linked?.id != null) return linked.id;
  return currentCompany.value?.id ?? null;
}

function resolveInitialSensorOption(): ISensor | null {
  const s = props.maintenance.sensor;
  if (!s?.id) return null;
  const fromList = sensorOptions.value.find((x) => String(x.id) === String(s.id));
  if (fromList) return fromList;
  return s;
}

function hasSensorsLoadedForCompany(companyId: number): boolean {
  return Object.prototype.hasOwnProperty.call(sensorStore.sensorsByCompany, companyId);
}

async function ensureEditPrerequisitesInStore() {
  if (listMaintenanceTypes.value.length === 0) {
    await getMaintenanceTypes();
  }

  if (isSuperAdminCompany.value) {
    if (!getAllCompanies.value?.length) {
      await fetchAllCompanies();
    }
    const companyId = inferInitialCompanyIdForSensors() ?? currentCompany.value?.id ?? null;
    if (companyId != null && !hasSensorsLoadedForCompany(companyId)) {
      await fetchSensorsByCompanyId(companyId);
    }
  } else {
    await fetchSensors();
  }
}

const editFormInitialValues = computed(() => {
  const mt = props.maintenance.maintenance_type;
  let maintenanceTypeOption = null;
  if (mt) {
    maintenanceTypeOption =
      listMaintenanceTypes.value.find((x) => String(x.id) === String(mt.id)) ?? {
        id: mt.id,
        name: mt.name,
        description: mt.description ?? '',
        category: mt.category ?? '',
        area: mt.area ?? '',
      };
  }
  const priorityOption =
    priorityOptions.value.find((p) => p.id === props.maintenance.priority) ?? null;
  const companyScopeId = inferInitialCompanyIdForSensors() ?? currentCompany.value?.id ?? null;
  const companyScopeOption =
    companyScopeId != null
      ? companies.value.find((c) => c.id === companyScopeId) ?? null
      : null;
  return {
    maintenance_type: maintenanceTypeOption,
    priority: priorityOption,
    description: props.maintenance.description ?? '',
    requires_shutdown: Boolean(props.maintenance.requires_shutdown),
    sensor: resolveInitialSensorOption(),
    company_scope: companyScopeOption,
  };
});

watch(
  () => props.maintenance.id,
  () => {
    isEditingOpenMaintenance.value = false;
    selectedCompanyIdForSensors.value = null;
  },
);

watch(isEditingOpenMaintenance, (editing) => {
  if (!editing) {
    selectedCompanyIdForSensors.value = null;
  }
});

function openDestroyConfirm() {
  showDestroyConfirm.value = true;
}

function confirmDestroy() {
  emit('destroy', props.maintenance.id);
  showDestroyConfirm.value = false;
}

function cancelDestroyConfirm() {
  showDestroyConfirm.value = false;
}

async function enterEditMode() {
  await ensureEditPrerequisitesInStore();
  if (isSuperAdminCompany.value) {
    selectedCompanyIdForSensors.value =
      inferInitialCompanyIdForSensors() ?? currentCompany.value?.id ?? null;
  }
  isEditingOpenMaintenance.value = true;
}

async function onEditCompanyScopeChange(option: ICompany) {
  const id = option?.id;
  if (id == null || Number.isNaN(Number(id))) return;
  if (selectedCompanyIdForSensors.value === id) return;

  selectedCompanyIdForSensors.value = id;
  await fetchSensorsByCompanyId(id);
  await nextTick();
  editMaintenanceFormRef.value?.setFieldValue('sensor', undefined, false);
}

function cancelEditMode() {
  isEditingOpenMaintenance.value = false;
}

async function onSubmitEdit(values: Record<string, unknown>) {
  const mt = values.maintenance_type as { id: string | number } | undefined | null;
  const pr = values.priority as { id: string } | undefined | null;
  const sens = values.sensor as ISensor | undefined | null;
  if (!mt?.id || !pr?.id) return;
  isSubmittingEdit.value = true;
  const payload: IUpdateMaintenance = {
    maintenance_type_id: String(mt.id),
    priority: pr.id,
    description: String(values.description ?? ''),
    requires_shutdown: Boolean(values.requires_shutdown),
  };
  if (sens?.id != null && String(sens.id).length > 0) {
    payload.sensor_id = String(sens.id);
    if (isSuperAdminCompany.value && selectedCompanyIdForSensors.value != null) {
      payload.sensor_company_id = selectedCompanyIdForSensors.value;
    }
  }
  const updated = await updateMaintenance(props.maintenance.id, payload);
  isSubmittingEdit.value = false;
  if (updated) {
    emit('maintenance-updated', updated);
    isEditingOpenMaintenance.value = false;
    await nextTick();
  }
}

const showReadDetailsBlock = computed(
  () => !(props.section === 'open' && canEditOpenMaintenance.value && isEditingOpenMaintenance.value),
);
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center px-4"
    @click="emit('close')"
  >
    <div class="absolute inset-0 bg-black/40" aria-hidden="true" />
    <Transition name="maintenance-modal-shell" appear mode="out-in">
      <div
        :key="maintenance.id"
        class="maintenance-modal-shell-item relative mx-auto flex max-h-[90vh] w-full max-w-md flex-col overflow-hidden bg-white shadow-xl md:max-w-lg lg:max-w-xl"
        role="dialog"
        aria-modal="true"
        @click.stop
      >
        <div class="h-1 shrink-0 bg-blue" />

        <div class="relative flex min-h-0 flex-1 flex-col p-6 sm:p-8">
          <button
            type="button"
            class="absolute right-5 top-5 text-gray-400 transition-colors hover:text-black"
            @click="emit('close')"
          >
            <XMarkIcon class="h-5 w-5" aria-hidden="true" />
          </button>

          <div class="mb-5 border-b border-stroke pr-8">
            <nav class="-mb-px flex gap-6" aria-label="Tabs">
              <button
                type="button"
                :class="[
                  'whitespace-nowrap border-b-2 py-3 text-sm font-medium transition duration-150 ease-in-out',
                  currentTab === 'details'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-body hover:border-stroke hover:text-black',
                ]"
                @click="emit('update:currentTab', 'details')"
              >
                {{ t('maintenance_user_listing.modal.tab_details') }}
              </button>
              <button
                type="button"
                :class="[
                  'whitespace-nowrap border-b-2 py-3 text-sm font-medium transition duration-150 ease-in-out',
                  currentTab === 'comments'
                    ? 'border-primary text-primary'
                    : 'border-transparent text-body hover:border-stroke hover:text-black',
                ]"
                @click="emit('update:currentTab', 'comments')"
              >
                {{ t('maintenance_user_listing.modal.tab_comments') }}
              </button>
            </nav>
          </div>

          <div v-if="currentTab === 'details'" class="flex min-h-0 flex-1 flex-col">
            <div class="mb-4 flex shrink-0 items-start justify-between gap-3 pr-2">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <CpuChipIcon class="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
                  <h2 class="truncate text-xl font-semibold text-black">
                    {{
                      isEditingOpenMaintenance && canEditOpenMaintenance && section === 'open'
                        ? t('maintenance_user_listing.modal.edit_maintenance_title')
                        : maintenanceSensorHeadline
                    }}
                  </h2>
                </div>
                <p
                  v-if="showReadDetailsBlock && isSuperAdminCompany && sensorCompanyNames"
                  class="mt-2 flex items-center gap-2 text-sm text-body"
                >
                  <BuildingOffice2Icon class="h-4 w-4 shrink-0" aria-hidden="true" />
                  <span>{{ sensorCompanyNames }}</span>
                </p>
              </div>
              <span
                v-if="showReadDetailsBlock && maintenance.priority"
                class="shrink-0 px-2 py-0.5 text-xs font-medium"
                :class="priorityBadgeClass(maintenance.priority)"
              >
                {{ t(`maintenance_user_listing.priority.${maintenance.priority}`) }}
              </span>
            </div>

            <div
              class="box-border min-h-[6rem] max-h-[min(58vh,30rem)] min-w-0 flex-1 overflow-x-hidden overflow-y-auto pr-1"
            >
              <Transition name="maintenance-detail-fade" mode="out-in">
                <div
                  v-if="showReadDetailsBlock"
                  :key="`read-${maintenance.id}-${maintenance.updated_at ?? ''}`"
                  class="maintenance-detail-fade-item grid grid-cols-[1.25rem_minmax(0,1fr)] items-start gap-x-3 gap-y-3 text-sm text-slate-700"
                >
                  <template v-if="maintenance.maintenance_type">
                    <WrenchScrewdriverIcon class="h-5 w-5 text-gray" aria-hidden="true" />
                    <span class="leading-5">{{ maintenance.maintenance_type.name }}</span>
                  </template>
                  <template v-if="maintenance.user?.name">
                    <UserIcon class="h-5 w-5 text-gray" aria-hidden="true" />
                    <span class="leading-5">{{ maintenance.user.name }}</span>
                  </template>
                  <template v-if="maintenance.description">
                    <DocumentTextIcon class="h-5 w-5 text-gray" aria-hidden="true" />
                    <span class="leading-5 whitespace-pre-wrap">{{ maintenance.description }}</span>
                  </template>
                  <template v-if="maintenance.started_at">
                    <ClockIcon class="h-5 w-5 text-gray" aria-hidden="true" />
                    <span class="leading-5">{{ formatDateWithTime(maintenance.started_at) }}</span>
                  </template>
                  <template v-if="maintenance.scheduled_for">
                    <CalendarDaysIcon class="h-5 w-5 text-gray" aria-hidden="true" />
                    <span class="leading-5">{{ formatDateWithTime(maintenance.scheduled_for) }}</span>
                  </template>
                  <template v-if="maintenance.finished_at">
                    <CheckCircleIcon class="h-5 w-5 text-gray" aria-hidden="true" />
                    <span class="leading-5">{{ formatDateWithTime(maintenance.finished_at) }}</span>
                  </template>
                  <template v-if="maintenance.actions_taken">
                    <ClipboardDocumentCheckIcon class="h-5 w-5 text-gray" aria-hidden="true" />
                    <span class="leading-5 whitespace-pre-wrap">{{ maintenance.actions_taken }}</span>
                  </template>
                </div>

                <Form
                  v-else
                  :key="`edit-${maintenance.id}`"
                  ref="editMaintenanceFormRef"
                  class="maintenance-detail-fade-item min-w-0 w-full max-w-full"
                  :validation-schema="MaintenanceOpenEditSchema"
                  :initial-values="editFormInitialValues"
                  @submit="onSubmitEdit"
                >
                  <div class="space-y-4 min-w-0 w-full max-w-full">
                    <div class="flex min-w-0 w-full max-w-full flex-col gap-4 sm:flex-row sm:items-start">
                      <div class="min-w-0 w-full max-w-full sm:basis-0 sm:flex-[2]">
                        <label class="mb-2 block text-sm font-medium text-slate-700">{{
                          t('maintenance_listing.body.table.columns.maintenance_type')
                        }}</label>
                        <Dropdown
                          name="maintenance_type"
                          :label="t('maintenance_listing.body.table.columns.maintenance_type')"
                          :options="listMaintenanceTypes"
                          :placeholder="t('maintenance_create.header.actions.placeholder.maintenance_type')"
                          label-attribute="name"
                          :already-selected-option="editFormInitialValues.maintenance_type ?? undefined"
                        />
                        <ErrorMessage name="maintenance_type" class="mt-1 block text-sm text-red" />
                      </div>
                      <div class="min-w-0 w-full max-w-full sm:basis-0 sm:flex-1">
                        <label class="mb-2 block text-sm font-medium text-slate-700">{{
                          t('maintenance_listing.body.table.columns.priority')
                        }}</label>
                        <Dropdown
                          name="priority"
                          :label="t('maintenance_listing.body.table.columns.priority')"
                          :options="priorityOptions"
                          :placeholder="t('maintenance_create.header.actions.placeholder.priority')"
                          label-attribute="name"
                          :already-selected-option="editFormInitialValues.priority ?? undefined"
                        />
                        <ErrorMessage name="priority" class="mt-1 block text-sm text-red" />
                      </div>
                    </div>
                    <div class="flex min-w-0 w-full max-w-full flex-col gap-4 sm:flex-row sm:items-start">
                      <div
                        v-if="isSuperAdminCompany && companies.length"
                        class="min-w-0 w-full max-w-full sm:basis-0 sm:flex-1"
                      >
                        <label class="mb-2 block text-sm font-medium text-slate-700">{{
                          t('maintenance_create.body.form.company')
                        }}</label>
                        <Dropdown
                          name="company_scope"
                          :label="t('maintenance_create.body.form.company')"
                          :options="companies"
                          :placeholder="t('maintenance_create.header.actions.placeholder.company')"
                          label-attribute="name"
                          :already-selected-option="selectedCompanyScopeForDropdown"
                          @option-selected="onEditCompanyScopeChange"
                        />
                        <ErrorMessage name="company_scope" class="mt-1 block text-sm text-red" />
                      </div>
                      <div class="min-w-0 w-full max-w-full sm:basis-0 sm:flex-1">
                        <label class="mb-2 block text-sm font-medium text-slate-700">{{
                          t('maintenance_listing.body.table.columns.sensor')
                        }}</label>
                        <DropdownSensors
                          name="sensor"
                          constrain-to-parent
                          :options="sensorOptions"
                          :already-selected-option="editFormInitialValues.sensor ?? undefined"
                        />
                        <ErrorMessage name="sensor" class="mt-1 block text-sm text-red" />
                      </div>
                    </div>
                    <div class="min-w-0 w-full max-w-full">
                      <label class="mb-2 block text-sm font-medium text-slate-700">{{
                        t('maintenance_listing.body.table.columns.description')
                      }}</label>
                      <Field name="description" v-slot="{ field, errorMessage }">
                        <textarea
                          v-bind="field"
                          rows="5"
                          class="box-border w-full min-w-0 max-w-full border border-stroke bg-white p-3 text-sm text-black outline-none focus:border-primary"
                          :placeholder="t('maintenance_listing.header.actions.placeholder.description')"
                        />
                        <p v-if="errorMessage" class="mt-1 text-sm text-red">{{ errorMessage }}</p>
                      </Field>
                    </div>
                    <div class="min-w-0 w-full max-w-full">
                      <Field name="requires_shutdown" type="checkbox" :value="true" :unchecked-value="false" v-slot="{ field }">
                        <label class="flex items-center gap-2">
                          <input
                            type="checkbox"
                            class="h-5 w-5 shrink-0 border border-stroke bg-white text-primary"
                            v-bind="field"
                          />
                          <span class="text-sm text-slate-700">{{
                            t('maintenance_create.body.form.requires_shutdown')
                          }}</span>
                        </label>
                      </Field>
                    </div>
                    <div class="flex min-w-0 w-full max-w-full flex-wrap justify-end gap-3 pt-2">
                      <button
                        type="button"
                        class="min-h-[2.5rem] border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        @click="cancelEditMode"
                      >
                        {{ t('maintenance_user_listing.modal.cancel_edit') }}
                      </button>
                      <button
                        type="submit"
                        :disabled="isSubmittingEdit"
                        class="min-h-[2.5rem] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
                      >
                        {{ isSubmittingEdit ? '…' : t('maintenance_user_listing.modal.save') }}
                      </button>
                    </div>
                  </div>
                </Form>
              </Transition>
            </div>

            <div
              v-if="section === 'open' && !isEditingOpenMaintenance"
              class="mt-5 flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-stroke pt-4"
            >
              <div class="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="min-h-[2.5rem] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
                  @click="emit('start', maintenance.id)"
                >
                  {{ t('maintenance_user_listing.modal.start') }}
                </button>
                <button
                  v-if="canDeleteMaintenance"
                  type="button"
                  class="min-h-[2.5rem] bg-red px-4 py-2 text-sm font-medium text-white hover:bg-red-600"
                  @click="openDestroyConfirm"
                >
                  {{ t('maintenance_user_listing.modal.destroy') }}
                </button>
                <button
                  v-else
                  type="button"
                  class="min-h-[2.5rem] border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  @click="emit('close')"
                >
                  {{ t('maintenance_user_listing.modal.close') }}
                </button>
              </div>
              <button
                v-if="canEditOpenMaintenance"
                type="button"
                class="ml-auto min-h-[2.5rem] border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 sm:ml-0"
                @click="enterEditMode"
              >
                {{ t('maintenance_user_listing.modal.edit') }}
              </button>
            </div>

            <div v-if="section === 'in_progress'" class="mt-5 shrink-0 border-t border-stroke pt-4">
              <label class="mb-2 block text-sm font-medium text-slate-700">{{
                t('maintenance_user_listing.body.actions_taken')
              }}</label>
              <textarea
                :value="actionsTaken"
                rows="4"
                class="w-full border border-stroke bg-white p-3 text-sm text-black outline-none focus:border-primary"
                @input="emit('update:actionsTaken', ($event.target as HTMLTextAreaElement).value)"
              />
              <div class="mt-3 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  class="min-h-[2.5rem] bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
                  @click="emit('finish', maintenance.id)"
                >
                  {{ t('maintenance_user_listing.modal.finish_maintenance') }}
                </button>
                <button
                  type="button"
                  class="min-h-[2.5rem] border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  @click="emit('close')"
                >
                  {{ t('maintenance_user_listing.modal.close') }}
                </button>
              </div>
            </div>

            <div v-if="section === 'finished' || section === 'done'" class="mt-5 shrink-0 border-t border-stroke pt-4">
              <button
                type="button"
                class="min-h-[2.5rem] border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                @click="emit('close')"
              >
                {{ t('maintenance_user_listing.modal.close') }}
              </button>
            </div>
          </div>

          <div v-if="currentTab === 'comments'" class="flex min-h-0 flex-1 flex-col">
            <div class="max-h-80 space-y-3 overflow-y-auto">
              <div
                v-for="comment in comments.slice(0, commentsLimit)"
                :key="comment.id"
                class="relative border border-stroke bg-gray-50 p-4"
              >
                <p class="pr-16 text-sm text-slate-800">{{ comment.comment }}</p>
                <div class="mt-2 flex flex-wrap items-center gap-3 text-xs text-body">
                  <span class="inline-flex items-center gap-1">
                    <UserIcon class="h-3.5 w-3.5" aria-hidden="true" />
                    {{ comment.user.name }}
                  </span>
                  <span class="inline-flex items-center gap-1">
                    <ClockIcon class="h-3.5 w-3.5" aria-hidden="true" />
                    {{ formatDateWithTime(comment.created_at) }}
                  </span>
                </div>
                <div class="absolute right-2 top-2 flex gap-1">
                  <button
                    type="button"
                    class="p-1.5 text-primary hover:bg-slate-100"
                    @click="emit('start-editing', comment)"
                  >
                    <PencilSquareIcon class="h-5 w-5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    class="p-1.5 text-red hover:bg-red-50"
                    @click="emit('confirm-delete-comment', comment.id)"
                  >
                    <TrashIcon class="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
              </div>
              <p v-if="comments.length === 0" class="py-6 text-center text-sm text-body">
                {{ t('maintenance_user_listing.modal.no_comments') }}
              </p>
              <button
                v-if="comments.length >= commentsLimit && comments.length > 0"
                type="button"
                class="mt-2 w-full border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-slate-50"
                @click="emit('load-more-comments')"
              >
                {{ t('maintenance_user_listing.modal.load_more_comments') }}
              </button>
            </div>

            <div class="mt-4 border-t border-stroke pt-4">
              <h3 class="mb-2 text-base font-semibold text-black">
                {{
                  editingCommentId !== null
                    ? t('maintenance_user_listing.modal.edit_comment')
                    : t('maintenance_user_listing.modal.add_comment')
                }}
              </h3>
              <textarea
                :value="newCommentText"
                rows="3"
                class="mt-1 w-full border border-stroke bg-white p-3 text-sm text-black outline-none focus:border-primary"
                :placeholder="t('maintenance_user_listing.modal.comment_placeholder')"
                @input="emit('update:newCommentText', ($event.target as HTMLTextAreaElement).value)"
              />
              <div class="mt-3 flex gap-3">
                <button
                  type="button"
                  class="min-h-[2.5rem] flex-1 bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
                  @click="editingCommentId !== null ? emit('edit-comment') : emit('add-comment')"
                >
                  {{
                    editingCommentId !== null
                      ? t('maintenance_user_listing.modal.save_comment_edit')
                      : t('maintenance_user_listing.modal.add')
                  }}
                </button>
                <button
                  v-if="editingCommentId !== null"
                  type="button"
                  class="min-h-[2.5rem] flex-1 border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  @click="emit('cancel-editing')"
                >
                  {{ t('maintenance_user_listing.modal.cancel') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </div>

  <div
    v-if="showDestroyConfirm"
    class="fixed inset-0 z-[60] flex items-center justify-center px-4"
  >
    <div class="absolute inset-0 bg-black/40" />
    <div class="relative w-full max-w-sm bg-white p-6 shadow-xl">
      <div class="absolute left-0 right-0 top-0 h-1 bg-blue" />
      <h3 class="mt-2 text-lg font-semibold text-black">
        {{ t('maintenance_user_listing.modal.destroy_confirm_title') }}
      </h3>
      <p class="mt-2 text-sm text-body">
        {{ t('maintenance_user_listing.modal.destroy_confirm_message') }}
      </p>
      <div class="mt-6 flex justify-end gap-3">
        <button
          type="button"
          class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          @click="cancelDestroyConfirm"
        >
          {{ t('maintenance_user_listing.modal.cancel') }}
        </button>
        <button
          type="button"
          class="bg-red px-4 py-2 text-sm font-medium text-white hover:bg-red-600"
          @click="confirmDestroy"
        >
          {{ t('maintenance_user_listing.modal.destroy') }}
        </button>
      </div>
    </div>
  </div>

  <div
    v-if="showDeleteConfirm"
    class="fixed inset-0 z-[60] flex items-center justify-center px-4"
  >
    <div class="absolute inset-0 bg-black/40" />
    <div class="relative w-full max-w-sm bg-white p-6 shadow-xl">
      <div class="absolute left-0 right-0 top-0 h-1 bg-blue" />
      <h3 class="mt-2 text-lg font-semibold text-black">
        {{ t('maintenance_user_listing.modal.destroy_confirm_title') }}
      </h3>
      <p class="mt-2 text-sm text-body">
        {{ t('maintenance_user_listing.modal.delete_comment_confirm_message') }}
      </p>
      <div class="mt-6 flex justify-end gap-3">
        <button
          type="button"
          class="border border-stroke px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          @click="emit('cancel-delete')"
        >
          {{ t('maintenance_user_listing.modal.cancel') }}
        </button>
        <button
          type="button"
          class="bg-red px-4 py-2 text-sm font-medium text-white hover:bg-red-600"
          @click="emit('confirm-delete')"
        >
          {{ t('maintenance_user_listing.modal.destroy') }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.maintenance-modal-shell-item {
  transform-origin: center center;
}

.maintenance-modal-shell-enter-active,
.maintenance-modal-shell-leave-active,
.maintenance-detail-fade-enter-active,
.maintenance-detail-fade-leave-active {
  transition:
    opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.maintenance-modal-shell-enter-from,
.maintenance-modal-shell-leave-to,
.maintenance-detail-fade-enter-from,
.maintenance-detail-fade-leave-to {
  opacity: 0;
  transform: translateY(10px) scale(0.98);
} 

.maintenance-modal-shell-enter-to,
.maintenance-modal-shell-leave-from,
.maintenance-detail-fade-enter-to,
.maintenance-detail-fade-leave-from {
  transform: translateY(0) scale(1);
}

.maintenance-detail-fade-item {
  min-height: 1px;
}
</style>
